import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { BLANK_WAREHOUSE_MISCELLANEOUS_FEE } from "../../constants"
import { useCreateWarehouse, useGetWarehouses, useUpdateWarehouse } from "../../hooks"
import type { WarehouseFormValues } from "../../schemas"
import { warehouseFormSchema, warehouseLocationFormSchema } from "../../schemas"
import { serializeWarehouseForm } from "../../utils"

import WarehouseInfoForm from "./WarehouseInfoForm"
import WarehouseLocationForm from "./WarehouseLocationForm"
import WarehouseLocationInfo from "./WarehouseLocationInfo"

import { Button } from "@/components/common/Button"
import { CancelDialog, ConfirmationDialog } from "@/components/common/Modals"
import type { Company, Location, WarehouseArea } from "@/graphql/codegen/graphql"
import { CompanyStatus, WarehouseType } from "@/graphql/codegen/graphql"
import { useGetBaseCompanies } from "@/hooks/useGetBaseCompanies"
import { useGetBaseLocations } from "@/hooks/useGetBaseLocations"
import { CompanyType } from "@/types/companyType"
import { toStringOrEmpty } from "@/utils/forms"

enum WarehouseFormDialogType {
  Form = "FORM",
  ConfirmLocation = "CONFIRM_LOCATION",
  CancelEdit = "CANCEL_EDIT",
}

enum WarehouseCreateFormStep {
  Location = "LOCATION",
  InfoAndFees = "INFO_AND_FEES",
}

interface WarehouseFormDialogProps {
  warehouse: WarehouseArea | null
  onSubmit: () => void
  onClose: () => void
}

const WarehouseFormDialog: FC<WarehouseFormDialogProps> = ({ warehouse, onSubmit, onClose }) => {
  const isCreateMode = !warehouse
  const [openedDialog, setOpenedDialog] = useState(WarehouseFormDialogType.Form)
  const [step, setStep] = useState(WarehouseCreateFormStep.Location)
  const [company, setCompany] = useState<Company | null>((warehouse?.location?.company ?? null) as Company | null)
  const [location, setLocation] = useState<Location | null>(warehouse?.location ?? null)
  const formMethods = useForm<WarehouseFormValues>({
    defaultValues: {
      companyId: toStringOrEmpty(warehouse?.location?.company?.companyId),
      locationId: toStringOrEmpty(warehouse?.location?.locationId),
      externalSystemId: toStringOrEmpty(warehouse?.externalSystemId),
      type: warehouse?.type ?? WarehouseType.AkrochemManaged,
      receivingHoursFrom: warehouse?.receivingHoursFrom ?? null,
      receivingHoursTo: warehouse?.receivingHoursTo ?? null,
      shippingHoursFrom: warehouse?.shippingHoursFrom ?? null,
      shippingHoursTo: warehouse?.shippingHoursTo ?? null,
      specialInstructions: toStringOrEmpty(warehouse?.specialInstructions),
      monthlyAccrualFee: toStringOrEmpty(warehouse?.monthlyAccrualFee),
      inboundFee: toStringOrEmpty(warehouse?.inboundFee),
      outboundFee: toStringOrEmpty(warehouse?.outboundFee),
      miscellaneousFees: warehouse?.miscellaneousFees?.length
        ? warehouse.miscellaneousFees.map((fee) => ({
            type: fee.type,
            amount: fee.amount.toString(),
          }))
        : [BLANK_WAREHOUSE_MISCELLANEOUS_FEE],
    },
    resolver: zodResolver(warehouseFormSchema),
    mode: "all",
  })
  const {
    watch,
    getValues,
    formState: { isDirty, isValid, errors },
    handleSubmit,
  } = formMethods
  const companiesMethods = useGetBaseCompanies({
    statuses: [CompanyStatus.Active],
    types: [CompanyType.AkrochemWarehouse, CompanyType.ConsignmentWarehouse],
  })
  const locationsMethods = useGetBaseLocations({ companyId: watch("companyId"), isLocationActive: true })
  const externalSystemId = watch("externalSystemId")
  const { warehouses, isFetching: isFetchingWarehouses } = useGetWarehouses({
    externalSystemIds: [externalSystemId],
    isPaused: externalSystemId.length !== 2,
  })
  const canContinue =
    warehouseLocationFormSchema.safeParse(getValues()).success && !isFetchingWarehouses && !errors.externalSystemId
  const { create, isCreating } = useCreateWarehouse({ onSuccess: onSubmit })
  const { update, isUpdating } = useUpdateWarehouse({ onSuccess: onSubmit })
  const canSubmit = isDirty && isValid && !(isCreating || isUpdating)

  const handleCompanySelect = (company: Company) => {
    setCompany(company)
    setLocation(null)
  }

  const handleLocationSelect = (location: Location) => setLocation(location)

  const handleContinue = () => {
    if (isCreateMode) {
      setOpenedDialog(WarehouseFormDialogType.ConfirmLocation)
    } else {
      setStep(WarehouseCreateFormStep.InfoAndFees)
    }
  }

  const handleBackToForm = () => {
    setOpenedDialog(WarehouseFormDialogType.Form)
  }

  const handleLocationConfirm = () => {
    setStep(WarehouseCreateFormStep.InfoAndFees)
    setOpenedDialog(WarehouseFormDialogType.Form)
  }

  const handleClose = () => {
    if (isDirty) {
      setOpenedDialog(WarehouseFormDialogType.CancelEdit)
    } else {
      onClose()
    }
  }

  const handleCancelConfirm = () => {
    onClose()
  }

  const submitForm = (formValues: WarehouseFormValues) => {
    const payload = serializeWarehouseForm(formValues)

    if (isCreateMode) {
      create({
        locationId: formValues.locationId,
        ...payload,
      })
    } else {
      update({
        warehouseAreaId: warehouse.warehouseAreaId,
        ...payload,
      })
    }
  }

  return (
    <>
      {openedDialog === WarehouseFormDialogType.Form && (
        <Dialog data-testid='warehouse-form-dialog' open={true} onClose={handleClose} fullWidth>
          <DialogTitle className='pl-10 font-normal text-primary'>
            {isCreateMode ? "Add" : "Edit"} Warehouse
          </DialogTitle>
          <IconButton className='absolute right-4 top-3' aria-label='Close' onClick={handleClose}>
            <CloseOutlined className='text-blue-gray' />
          </IconButton>
          <Divider />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...formMethods}>
              {step === WarehouseCreateFormStep.Location && (
                <WarehouseLocationForm
                  warehouse={warehouse}
                  warehouses={warehouses}
                  companiesMethods={companiesMethods}
                  locationsMethods={locationsMethods}
                  onCompanySelect={handleCompanySelect}
                  onLocationSelect={handleLocationSelect}
                />
              )}
              {step === WarehouseCreateFormStep.InfoAndFees && (
                <WarehouseInfoForm company={company!} location={location!} externalSystemId={externalSystemId} />
              )}
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button data-testid='warehouse-form-cancel-button' appearance='outlined' onClick={handleClose}>
              Cancel
            </Button>
            {step === WarehouseCreateFormStep.Location && (
              <Button
                data-testid='warehouse-form-continue-button'
                variant='primary'
                onClick={handleContinue}
                disabled={!canContinue}
              >
                Continue
              </Button>
            )}
            {step === WarehouseCreateFormStep.InfoAndFees && (
              <Button
                data-testid='warehouse-form-submit-button'
                variant='primary'
                disabled={!canSubmit}
                onClick={handleSubmit(submitForm)}
              >
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
      {openedDialog === WarehouseFormDialogType.ConfirmLocation && (
        <ConfirmationDialog
          title='Are you sure you want to set this location as a warehouse?'
          content={
            <WarehouseLocationInfo company={company!} location={location!} externalSystemId={externalSystemId} />
          }
          closeLabel='Cancel'
          confirmLabel='Yes, Continue'
          onClose={handleBackToForm}
          onConfirm={handleLocationConfirm}
        />
      )}
      {openedDialog === WarehouseFormDialogType.CancelEdit && (
        <CancelDialog onClose={handleBackToForm} onConfirm={handleCancelConfirm} />
      )}
    </>
  )
}

export default WarehouseFormDialog
